// TanStack Query Configuration
// /app/frontend/src/lib/queryClient.js

import { QueryClient } from '@tanstack/react-query';

// Create a client with optimal settings for a booking platform
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Stale time - how long data is considered fresh (5 minutes)
      staleTime: 5 * 60 * 1000,
      
      // Cache time - how long inactive data stays in cache (30 minutes)
      gcTime: 30 * 60 * 1000,
      
      // Retry failed requests up to 3 times
      retry: 3,
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
      
      // Refetch settings
      refetchOnWindowFocus: true,
      refetchOnReconnect: true,
      refetchOnMount: true,
      
      // Network mode
      networkMode: 'online',
    },
    mutations: {
      // Retry mutations once
      retry: 1,
      
      // Network mode
      networkMode: 'online',
    },
  },
});

// Query key factory for consistent cache keys
export const queryKeys = {
  // Venues
  venues: {
    all: ['venues'],
    list: () => [...queryKeys.venues.all, 'list'],
    detail: (id) => [...queryKeys.venues.all, 'detail', id],
    active: () => [...queryKeys.venues.all, 'active'],
  },
  
  // Bookables
  bookables: {
    all: ['bookables'],
    list: () => [...queryKeys.bookables.all, 'list'],
    detail: (id) => [...queryKeys.bookables.all, 'detail', id],
    byVenue: (venueId) => [...queryKeys.bookables.all, 'venue', venueId],
    availability: (bookableId, date) => [...queryKeys.bookables.all, 'availability', bookableId, date],
  },
  
  // Bookings
  bookings: {
    all: ['bookings'],
    list: (filters) => [...queryKeys.bookings.all, 'list', filters],
    detail: (id) => [...queryKeys.bookings.all, 'detail', id],
    userBookings: (userId) => [...queryKeys.bookings.all, 'user', userId],
    todaySchedule: () => [...queryKeys.bookings.all, 'today'],
  },
  
  // Users
  users: {
    all: ['users'],
    list: () => [...queryKeys.users.all, 'list'],
    detail: (id) => [...queryKeys.users.all, 'detail', id],
    me: () => [...queryKeys.users.all, 'me'],
  },
  
  // Organizations
  organizations: {
    all: ['organizations'],
    list: () => [...queryKeys.organizations.all, 'list'],
    detail: (id) => [...queryKeys.organizations.all, 'detail', id],
  },
  
  // Credit Wallets
  creditWallets: {
    all: ['creditWallets'],
    list: () => [...queryKeys.creditWallets.all, 'list'],
    detail: (id) => [...queryKeys.creditWallets.all, 'detail', id],
    userWallet: (userId) => [...queryKeys.creditWallets.all, 'user', userId],
  },
  
  // Analytics
  analytics: {
    all: ['analytics'],
    dashboard: (period) => [...queryKeys.analytics.all, 'dashboard', period],
    comprehensive: (params) => [...queryKeys.analytics.all, 'comprehensive', params],
    founders: () => [...queryKeys.analytics.all, 'founders'],
    finance: (reportType, period) => [...queryKeys.analytics.all, 'finance', reportType, period],
  },
  
  // Center Manager
  centerManager: {
    all: ['centerManager'],
    stats: () => [...queryKeys.centerManager.all, 'stats'],
    todaySchedule: () => [...queryKeys.centerManager.all, 'todaySchedule'],
  },

  // Holidays
  holidays: {
    all: ['holidays'],
    list: () => [...queryKeys.holidays.all, 'list'],
  },

  // Client Leads (Client Database)
  clientLeads: {
    all: ['clientLeads'],
    list: () => [...queryKeys.clientLeads.all, 'list'],
    callLogs: (leadId) => [...queryKeys.clientLeads.all, 'callLogs', leadId],
    uploadBatches: () => [...queryKeys.clientLeads.all, 'uploadBatches'],
    callStats: (params) => [...queryKeys.clientLeads.all, 'callStats', params],
    callHistory: (params) => [...queryKeys.clientLeads.all, 'callHistory', params],
  },
};

// Invalidation helpers
export const invalidateQueries = {
  venues: () => queryClient.invalidateQueries({ queryKey: queryKeys.venues.all }),
  bookables: () => queryClient.invalidateQueries({ queryKey: queryKeys.bookables.all }),
  bookings: () => queryClient.invalidateQueries({ queryKey: queryKeys.bookings.all }),
  users: () => queryClient.invalidateQueries({ queryKey: queryKeys.users.all }),
  organizations: () => queryClient.invalidateQueries({ queryKey: queryKeys.organizations.all }),
  creditWallets: () => queryClient.invalidateQueries({ queryKey: queryKeys.creditWallets.all }),
  analytics: () => queryClient.invalidateQueries({ queryKey: queryKeys.analytics.all }),
  holidays: () => queryClient.invalidateQueries({ queryKey: queryKeys.holidays.all }),
  clientLeads: () => queryClient.invalidateQueries({ queryKey: queryKeys.clientLeads.all }),

  // Invalidate all booking-related data (after booking creation/cancellation)
  bookingRelated: () => {
    queryClient.invalidateQueries({ queryKey: queryKeys.bookings.all });
    queryClient.invalidateQueries({ queryKey: queryKeys.bookables.all });
    queryClient.invalidateQueries({ queryKey: queryKeys.creditWallets.all });
    queryClient.invalidateQueries({ queryKey: queryKeys.analytics.all });
  },
  
  // Invalidate everything
  all: () => queryClient.invalidateQueries(),
};

export default queryClient;
